import React from 'react';
import { Box, Typography, Chip, Paper, IconButton } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { useFavoritesStore, useAuthStore } from '@/store';
import { useMeFavorites, useAddMatchFavorite, useRemoveMatchFavorite } from '@/hooks/useApi';
import type { Match, Team, Player } from '@/types';
import { matchScoreView } from '@/utils/matchScore';

interface MatchCardProps {
  match: Match;
  showTournament?: boolean;
  compact?: boolean;
  onClick?: () => void;
  resolveParticipantName?: (participantId: string) => string;
}

const formatTime = (dateString?: string | null): string => {
  if (!dateString) return '--:--';
  const date = new Date(dateString);
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};

const getParticipantName = (participant: Team | Player | undefined): string => {
  if (!participant) return 'TBD';
  if ('firstName' in participant) {
    return `${participant.firstName} ${participant.lastName || ''}`.trim();
  }
  return participant.name;
};

export const MatchCard: React.FC<MatchCardProps> = ({
  match,
  showTournament = false,
  compact = false,
  onClick,
  resolveParticipantName,
}) => {
  const { isAuthenticated } = useAuthStore();
  const { data: apiFavorites } = useMeFavorites({ enabled: isAuthenticated });
  const addApi = useAddMatchFavorite();
  const removeApi = useRemoveMatchFavorite();
  const { isMatchFavorite, addMatch, removeMatch } = useFavoritesStore();

  const isFavoriteFromApi = apiFavorites?.matches?.some((m) => (m as { id: string }).id === match.id);
  const isFavorite = isAuthenticated ? !!isFavoriteFromApi : isMatchFavorite(match.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAuthenticated) {
      if (isFavorite) removeApi.mutate(match.id);
      else addApi.mutate(match.id);
    } else {
      if (isFavorite) removeMatch(match.id);
      else addMatch(match.id);
    }
  };

  const homeParticipant = match.homeId && match.participants
    ? match.participants.find((p: Team | Player) => p.id === match.homeId)
    : undefined;
  const awayParticipant = match.awayId && match.participants
    ? match.participants.find((p: Team | Player) => p.id === match.awayId)
    : undefined;
  const homeName = homeParticipant
    ? getParticipantName(homeParticipant)
    : match.homeName
      || (match.homeId ? (resolveParticipantName?.(match.homeId) ?? 'Участник') : 'TBD');
  const awayName = awayParticipant
    ? getParticipantName(awayParticipant)
    : match.awayName
      || (match.awayId ? (resolveParticipantName?.(match.awayId) ?? 'Участник') : 'TBD');

  const { home: homeScore, away: awayScore } = matchScoreView(match.score);
  const showDashInsteadOfScore = match.status === 'scheduled';
  const homeScoreDisplay = showDashInsteadOfScore ? '-' : String(homeScore);
  const awayScoreDisplay = showDashInsteadOfScore ? '-' : String(awayScore);

  if (compact) {
    return (
      <Paper
        onClick={onClick}
        elevation={0}
        sx={{
          p: { xs: 0.75, md: 1 },
          mb: 0,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: 'transparent',
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 40, textAlign: 'center' }}>
          {match.status === 'live' ? (
            <Chip
              label="LIVE"
              size="small"
              sx={{
                bgcolor: '#ff4444',
                color: 'white',
                fontSize: '0.6rem',
                height: 18,
                fontWeight: 'bold',
              }}
            />
          ) : match.status === 'finished' ? (
            <Typography variant="caption" color="text.primary">
              {formatDate(match.scheduledAt)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.primary">
              {formatTime(match.scheduledAt)}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" noWrap fontWeight={600} color="text.primary">
            {homeName}
          </Typography>
          <Typography variant="body2" noWrap fontWeight={600} color="text.primary">
            {awayName}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 30 }}>
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            {homeScoreDisplay}
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            {awayScoreDisplay}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleFavoriteClick}
          sx={{ p: 0.25, color: isFavorite ? 'warning.main' : 'text.secondary' }}
        >
          {isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
        </IconButton>
      </Paper>
    );
  }

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 1.5,
        mb: 0.5,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'background.paper',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {/* Time/Status Column */}
        <Box sx={{ minWidth: 50, textAlign: 'center' }}>
          {match.status === 'live' ? (
            <>
              <Chip
                label="LIVE"
                size="small"
                sx={{
                  bgcolor: '#ff4444',
                  color: 'white',
                  fontSize: '0.65rem',
                  height: 20,
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              />
              <Typography variant="caption" color="error" fontWeight="bold">
                {match.score?.periods?.length || 1}'
              </Typography>
            </>
          ) : match.status === 'finished' ? (
            <>
              <Typography variant="caption" color="success.main" fontWeight="medium">
                {formatDate(match.scheduledAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatTime(match.scheduledAt)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" fontWeight="medium">
                {formatTime(match.scheduledAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(match.scheduledAt)}
              </Typography>
            </>
          )}
        </Box>

        {/* Teams Column */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={match.status === 'finished' && homeScore > awayScore ? 'bold' : 'normal'}>
              {homeName}
            </Typography>
            {match.status === 'finished' && homeScore > awayScore && (
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  ml: 1,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight={match.status === 'finished' && awayScore > homeScore ? 'bold' : 'normal'}>
              {awayName}
            </Typography>
            {match.status === 'finished' && awayScore > homeScore && (
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  ml: 1,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Score Column */}
        <Box sx={{ textAlign: 'right', minWidth: 40 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={match.status === 'live' ? 'error' : 'text.primary'}
          >
            {homeScoreDisplay}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={match.status === 'live' ? 'error' : 'text.primary'}
          >
            {awayScoreDisplay}
          </Typography>
        </Box>

        {/* Favorite Button */}
        <IconButton
          size="small"
          onClick={handleFavoriteClick}
          sx={{ p: 0.5, color: isFavorite ? 'warning.main' : 'text.secondary' }}
        >
          {isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
        </IconButton>
      </Box>

      {/* Additional Info */}
      {showTournament && match.tournamentId && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Турнир ID: {match.tournamentId}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
